import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "./client";
import { extractErrorMessage } from "./errors";

// Server-side CSV exports (API Design §4.11, §4.12, §14.3). Starting one
// returns at once; the file is written in the background, so the hook polls
// until it completes and then hands the browser a short-lived download URL.
// The server applies the list's own filters across every matching row, so
// an export is never capped by what the browser could hold.

export type ExportKind = "devices" | "admin_devices" | "audit";
export type ExportStatus = "processing" | "completed" | "failed";

export interface ExportJob {
  exportId: string;
  kind: ExportKind;
  status: ExportStatus;
  rowCount: number;
  downloadUrl?: string;
  downloadExpiry?: string;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

const START_PATHS: Record<ExportKind, string> = {
  devices: "/api/v1/devices/export",
  admin_devices: "/api/v1/admin/devices/export",
  audit: "/api/v1/audit-logs/export",
};

// A cross-tenant export is started (and so recorded) under the platform
// tenant on an admin path, which never carries the acting-company header;
// it has to be polled on an admin path too.
function pollPath(kind: ExportKind, exportId: string): string {
  const id = encodeURIComponent(exportId);
  return kind === "admin_devices" ? `/api/v1/admin/exports/${id}` : `/api/v1/exports/${id}`;
}

export async function startExport(kind: ExportKind, filters: URLSearchParams): Promise<ExportJob> {
  const query = filters.toString();
  const res = await apiFetch<{ data: ExportJob }>(`${START_PATHS[kind]}${query ? `?${query}` : ""}`, { method: "POST" });
  return res.data;
}

export async function getExport(kind: ExportKind, exportId: string): Promise<ExportJob> {
  const res = await apiFetch<{ data: ExportJob }>(pollPath(kind, exportId));
  return res.data;
}

// Poll quickly at first (a tenant export usually finishes in seconds), then
// ease off for a long cross-tenant one.
export function pollInterval(pollsSoFar: number): number {
  return Math.min(2000 + pollsSoFar * 1000, 10000);
}

// downloadFile opens a pre-signed URL. The object is served with
// Content-Disposition: attachment (which names the file), so the browser
// saves it and the page stays where it is. `download` does the same for a
// same-origin URL such as the dev mocks' blob URLs.
export function downloadFile(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

interface CsvExportCallbacks {
  // The file has been handed to the browser.
  onDone?: (job: ExportJob) => void;
  onError?: (message: string) => void;
}

// useCsvExport starts an export of `kind` and follows it to the download.
// `running` covers the whole span from the click to the file; `rowCount` is
// the rows written so far, for progress.
export function useCsvExport(kind: ExportKind, callbacks: CsvExportCallbacks = {}) {
  // running is true from the click to the outcome. busy guards start()
  // synchronously, so a double click can't begin two exports.
  const [running, setRunning] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const busy = useRef(false);
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  function finish() {
    busy.current = false;
    setRunning(false);
    setExportId(null);
  }

  const start = useMutation({
    mutationFn: (filters: URLSearchParams) => startExport(kind, filters),
    onSuccess: (job) => setExportId(job.exportId),
    onError: (err) => {
      finish();
      callbacksRef.current.onError?.(errorMessage(err));
    },
  });

  const poll = useQuery({
    queryKey: ["exports", kind, exportId],
    queryFn: () => getExport(kind, exportId as string),
    enabled: exportId !== null,
    refetchInterval: (query) => (query.state.data?.status === "processing" ? pollInterval(query.state.dataUpdateCount) : false),
    refetchIntervalInBackground: true,
    gcTime: 0,
    retry: 2,
  });

  const job = exportId ? poll.data : undefined;
  useEffect(() => {
    if (!job || job.status === "processing") return;
    finish();
    if (job.status === "completed" && job.downloadUrl) {
      downloadFile(job.downloadUrl);
      callbacksRef.current.onDone?.(job);
    } else {
      callbacksRef.current.onError?.(job.error || "The export could not be completed.");
    }
  }, [job]);

  const pollError = exportId ? poll.error : null;
  useEffect(() => {
    if (!pollError) return;
    finish();
    callbacksRef.current.onError?.(errorMessage(pollError));
  }, [pollError]);

  return {
    start: (filters: URLSearchParams) => {
      if (busy.current) return;
      busy.current = true;
      setRunning(true);
      start.mutate(filters);
    },
    running,
    rowCount: job?.rowCount ?? 0,
  };
}

// The server's message for a refused export (e.g. a bad filter); a generic
// one for anything else.
function errorMessage(err: unknown): string {
  return err instanceof ApiError ? extractErrorMessage(err) : "Export failed. Try again.";
}
